import React, { useEffect, useRef, useState } from 'react';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Form } from '@components/atoms/Form';
import styles from './OTPCodeStep.module.css';
import { useTranslation } from 'react-i18next';
import { useOtpStep } from '@services/useOtpStep';
import { useOutletContext } from 'react-router-dom';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';

export interface OTPCodeStepProps {
    email: string;
    onNext: () => void;
}

interface OtpErrors {
    otp: string[];
}

const OTP_LENGTH = 6;
export const RESEND_TIMEOUT = 10;

export const OTPCodeStep: React.FC<OTPCodeStepProps> = () => {
    const { t } = useTranslation();
    const formRef = useRef<HTMLFormElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const { setOtpStep } = useData();
    const { draft } = useData();
    const draftUuid = draft?.uuid;
    //const [error, setError] = useState<string | null>(null);
    const [countdown, setCountdown] = useState<number>(RESEND_TIMEOUT);
    const [resendAvailable, setResendAvailable] = useState<boolean>(false);
    const { resendOtp } = useOtpStep();
    // Safe fallback for Storybook / tests
    const context = useOutletContext<OTPCodeStepProps | null>();
    const email = context?.email || 'default@example.com';
    const onNext = context?.onNext || (() => {});
    const { showDefaultNotification } = useNotifications();

    const { request: requestOtpStep, error, validationErrors, lastResponseTime } = useOtpStep();
    const [errors, setErrors] = useState<OtpErrors>({
        otp: [],
    });

    const translations = {
        title: t('otp.title'),
        codeInsert: t('otp.code'),
        error: t('otp.error'),
        countdown: t('otp.countdown'),
        resend: t('otp.resend'),
    };

    useEffect(() => {
        if (otp.every((d) => d !== '')) {
            handleSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otp]);

    useEffect(() => {
        if (countdown <= 0) {
            setResendAvailable(true);
            return;
        }
        const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    useEffect(() => {
        if (lastResponseTime) {
            if (validationErrors) {
                setErrors({
                    otp: validationErrors['otp'] || [],
                });
            } else if (error) {
                showDefaultNotification({ type: 'error', message: error });
            } else {
                onNext();
            }
        }
    }, [lastResponseTime]);

    const focusInput = (index: number) => {
        const inputs = containerRef.current?.querySelectorAll('input');
        if (inputs && inputs[index]) {
            (inputs[index] as HTMLInputElement).focus();
        }
    };

    const handleChange = (value: string, index: number) => {
        const digits = value.replace(/\D/g, '');
        if (!digits) return;

        const updated = [...otp];
        updated[index] = digits[0];

        for (let i = 1; i < digits.length && index + i < OTP_LENGTH; i++) {
            updated[index + i] = digits[i];
        }

        setOtp(updated);

        const nextIndex = index + digits.length;
        if (nextIndex < OTP_LENGTH) {
            focusInput(nextIndex);
        }
    };

    const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const inputs = containerRef.current?.querySelectorAll('input');
        if (!inputs) return;

        const activeElement = document.activeElement;
        const index = Array.from(inputs).findIndex((input) => input === activeElement);

        if (index === -1) return;

        if (e.key === 'Backspace') {
            e.preventDefault();
            const updated = [...otp];

            if (otp[index]) {
                updated[index] = '';
                setOtp(updated);
            } else if (index > 0) {
                updated[index - 1] = '';
                setOtp(updated);
                focusInput(index - 1);
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            focusInput(index - 1);
        } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
            e.preventDefault();
            focusInput(index + 1);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
        if (!pasted) return;

        const digits = pasted.slice(0, OTP_LENGTH).split('');
        const updated = [...otp];
        digits.forEach((digit, i) => {
            updated[i] = digit;
        });
        setOtp(updated);

        if (digits.length < OTP_LENGTH) {
            focusInput(digits.length);
        }
    };

    const handleSubmit = () => {
        const joined = otp.join('');
        if (joined.length !== OTP_LENGTH || otp.includes('')) {
            // setErrors(translations.error);
            return;
        }

        setErrors({ otp: [] });

        requestOtpStep({
            otp: joined,
        });
    };

    const handleResend = () => {
        resendOtp()
            .then(() => {
                setOtp(Array(OTP_LENGTH).fill(''));
                setCountdown(RESEND_TIMEOUT);
                setResendAvailable(false);
                focusInput(0);
            })
            .catch((err) => console.error(err));
    };

    return (
        <Form formRef={formRef} classes="d-flex flex-column gap-2" onEnterPress={handleSubmit} onSubmit={handleSubmit}>
            <>
                <Typography align="center" fontSize="24" fontWeight="800" color="dark">
                    {translations.title}
                </Typography>

                <Typography align="center" fontSize="16" fontWeight="400" color="muted" classes={styles.inputMargin}>
                    {translations.codeInsert}
                </Typography>

                <Typography align="center" fontSize="16" fontWeight="800" color="muted">
                    {email}
                </Typography>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <div className={styles.otpOuterWrapper}>
                    <div className={styles.otpColumnWrapper}>
                        <div
                            ref={containerRef}
                            onPaste={handlePaste}
                            onKeyDown={handleContainerKeyDown}
                            className={styles.otpRow}>
                            {otp.map((digit, index) => (
                                <Input
                                    key={index}
                                    type={InputTypes.text}
                                    variant={InputVariants.regular}
                                    value={digit}
                                    onChange={(value) => handleChange(value, index)}
                                    maxLength={1}
                                    classes={`${styles.inputColor} text-center`}
                                />
                            ))}
                        </div>

                        {error && (
                            <Typography color="danger" fontSize="14" classes={styles.errorAlignLeft}>
                                {error}
                            </Typography>
                        )}
                    </div>
                </div>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <div style={{ textAlign: 'center' }}>
                    {resendAvailable ? (
                        <Button
                            onClick={handleResend}
                            label={translations.resend}
                            mode={ButtonMode.primary}
                            data-testid="resend-button"
                        />
                    ) : (
                        <Typography align="center" fontSize="16" fontWeight="400" color="muted">
                            <>
                                {`${translations.countdown} `}
                                <Typography tagVariant={TypographyTagVariants.span} fontWeight="800" color="muted">
                                    {`${countdown}`}
                                </Typography>
                            </>
                        </Typography>
                    )}
                </div>
            </>
        </Form>
    );
};
